<template>
  <div v-if="size === 'sm'" class="space-y-1">
    <div :id="id" class="w-[110px] h-[60px]"/>
    <div v-if="!hideText" :title="year">
      <div class="font-semibold text-blue dark:text-white">
        {{ value }}
      </div>
      <div class="font-normal text-gray-500 text-xs">
        {{ year }}
      </div>
    </div>
  </div>

  <div v-else-if="size === 'md'">
    <div class="flex flex-col justify-between h-full">
      <div v-if="!hideText" class="leading-none px-3 py-2">
        <div class="text-2xl font-semibold text-blue dark:text-white">
          {{ value }}
        </div>
        <div class="text-sm whitespace-nowrap text-gray-500">
          {{ year }}
        </div>
      </div>
      <div
        :id="id"
        class="min-h-[90px] md:min-h-[110px] size-full rounded-b-lg border-t"
      />
    </div>
  </div>

  <card v-else :href="url">
    <template #header>
      <slot v-if="$slots.header" name="header"/>

      <component :is="`h${level}`" v-else-if="title" class="font-medium text-blue dark:text-white">
        {{ title }}
        <term v-if="help" :text="help"/>
      </component>
    </template>

    <div class="flex flex-col justify-between h-full">
      <div class="leading-none px-3 py-2">
        <div class="text-2xl font-semibold text-blue dark:text-white">
          {{ value }}
        </div>
        <div class="text-sm whitespace-nowrap text-gray-500">
          {{ year }}
        </div>
      </div>
      <div
        :id="id"
        class="w-full h-[90px] md:h-[110px] rounded-b-lg border-t"
      />
    </div>
  </card>
</template>

<script setup>
import Card from "@/components/cards/Card.vue"
import Highcharts from "@/highcharts"
import Term from "@/components/Term.vue"
import colors from "@/colors"
import { onMounted } from "vue"

const props = defineProps({
  options: { type: Object, required: true },
  value: { type: [String, Number] },
  year: { type: [String, Number] },
  id: { type: String, required: true },
  color: { type: String, default: colors.blue[500] },
  size: { type: String, default: "sm" },
  title: { type: String },
  level: { type: Number, default: 2 },
  help: { type: String },
  url: { type: String },
  hideText: { type: Boolean },
  max: { type: Number, default: 100 },
})

onMounted(() => {
  const { beforeChartFormat } = Highcharts.defaultOptions.accessibility.screenReaderSection
  const { color, max } = props
  const options = {
    accessibility: {
      screenReaderSection: {
        beforeChartFormat: beforeChartFormat.replace("<{headingTagName}>{chartTitle}</{headingTagName}>", ""),
      },
    },
    chart: {
      margin: [0, 0, 0, 0],
      backgroundColor: "transparent",
    },
    title: { text: "" },
    xAxis: { labels: { enabled: false }, minPadding: 0, maxPadding: 0 },
    yAxis: {
      maxPadding: 0,
      minPadding: 0,
      gridLineWidth: 0,
      endOnTick: false,
      labels: { enabled: false },
      min: 0,
      max,
    },
    legend: { enabled: false },
    tooltip: { enabled: false },
    plotOptions: {
      series: {
        animation: false,
        enableMouseTracking: true,
        lineWidth: 2,
        shadow: false,
        states: {
          hover: {
            lineWidth: 2,
            halo: {
              size: 9,
              attributes: {
                fill: color,
                stroke: color,
              },
            },
          },
        },
        marker: {
          radius: 0,
          states: {
            hover: {
              radius: 2,
              fillColor: color,
            },
          },
        },
        lineColor: color,
        fillColor: `${color}20`,
      },
    },
    ...props.options,
  }

  new Highcharts.Chart(props.id, options)
})
</script>
